import { useI18n } from "@nelurio/i18n";
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, isDemoMode } from "../api/client";
import { playStartSound, playTimerDoneSound, startForestAmbience, stopForestAmbience, type AmbienceKind } from "./sounds";

const STORAGE_KEY = "nelurio.focus.v1";
const SOUND_PREF_KEY = "nelurio.focus.sound";
const SOUND_KIND_KEY = "nelurio.focus.soundKind";

interface FocusPersisted {
  durationMinutes: number;
  remainingMs: number;
  running: boolean;
  paused: boolean;
  courseId: string;
  goal: string;
  treeKind?: string;
}

interface FocusTimerValue {
  durationMinutes: number;
  secondsLeft: number;
  running: boolean;
  paused: boolean;
  courseId: string;
  goal: string;
  lastSavedAt: number;
  ambienceEnabled: boolean;
  ambienceKind: AmbienceKind;
  setAmbienceEnabled: (value: boolean) => void;
  setAmbienceKind: (kind: AmbienceKind) => void;
  treeKind: string;
  start: (durationMinutes: number, courseId: string, goal: string, treeKind?: string) => void;
  pause: () => void;
  resume: () => void;
  stopAndSave: () => Promise<void>;
  discard: () => void;
}

const FocusTimerContext = createContext<FocusTimerValue | null>(null);

function load(): FocusPersisted {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as FocusPersisted;
      if (parsed && typeof parsed.remainingMs === "number") return parsed;
    }
  } catch { /* corrupted state falls through */ }
  return { durationMinutes: 25, remainingMs: 25 * 60_000, running: false, paused: false, courseId: "", goal: "" };
}


const PENDING_KEY = "nelurio.focus.pendingSave";
function stashPendingSave(payload: unknown) {
  try { window.localStorage.setItem(PENDING_KEY, JSON.stringify(payload)); } catch { /* private mode */ }
}
function takePendingSave(): unknown | null {
  try {
    const raw = window.localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    window.localStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw);
  } catch { return null; }
}

/** Browser notification for a finished session (asks permission on first use).
 *  Module scope — no hook access — so the copy comes pre-localized from the
 *  caller (useI18n inside the provider) and digits via Intl with the UI locale. */
function notifySessionDone(minutes: number, title: string, bodyTemplate: string, locale: string) {
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") void Notification.requestPermission();
    if (Notification.permission === "granted") {
      const minutesLabel = new Intl.NumberFormat(locale === "en" ? "en-GB" : locale).format(minutes);
      new Notification(title, {
        body: bodyTemplate.replace("{{minutes}}", minutesLabel),
        tag: "nelurio-focus-done",
        silent: true
      });
    }
  } catch { /* notifications unavailable */ }
}

export function FocusTimerProvider({ children }: { children: React.ReactNode }) {
  const { t, locale } = useI18n();
  const [state, setState] = useState<FocusPersisted>(load);
  // BACKGROUND PIPELINE (owner law): a running focus session must survive the
  // app being closed and still land its end notification in the phone's bar.
  // Two paths share the persisted wall-clock deadline (lib/focusBackground):
  // a) app closed past the deadline → on next bootstrap, save the full session
  //    server-side (the server fires the VAPID push) and clear the deadline;
  // b) app open (even backgrounded) → a wall-clock watch fires the local
  //    notification at the deadline — setInterval throttling cannot delay it.
  useEffect(() => {
    const record = readFocusEnd();
    if (!record) return;
    if (deadlineMissed(record, isFocusNotified(), Date.now())) {
      if (!isDemoMode()) {
        const payload = { courseId: record.courseId, goal: record.goal || "Focus", plannedMinutes: record.durationMinutes, actualSeconds: record.durationMinutes * 60, status: "completed", treeType: record.treeKind || "pine" };
        void api("/focus-sessions", { method: "POST", body: JSON.stringify(payload) })
          .then(() => setLastSavedAt(Date.now()))
          .catch(() => stashPendingSave(payload));
      }
      markFocusNotified();
      storeFocusEnd(null);
      notifySessionDone(record.durationMinutes, t("focus.doneTitle"), t("focus.doneBody"), locale);
      return;
    }
    clearFocusNotified();
    const watcher = window.setInterval(() => {
      const current = readFocusEnd();
      if (!current) { window.clearInterval(watcher); return; }
      if (remainingUntilDeadline(current, Date.now()) === 0) {
        markFocusNotified();
        storeFocusEnd(null);
        notifySessionDone(current.durationMinutes, t("focus.doneTitle"), t("focus.doneBody"), locale);
        window.clearInterval(watcher);
      }
    }, 5000);
    return () => window.clearInterval(watcher);
  }, [locale]);

  // App entry: retry a focus session whose save failed last time (tree + stats
  // used to be lost forever when the POST failed after the timer hit zero).
  useEffect(() => {
    const pending = takePendingSave();
    if (pending && !isDemoMode()) {
      void api("/focus-sessions", { method: "POST", body: JSON.stringify(pending) })
        .then(() => setLastSavedAt(Date.now()))
        .catch(() => stashPendingSave(pending));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [secondsLeft, setSecondsLeft] = useState(() => Math.ceil(state.remainingMs / 1000));
  const [lastSavedAt, setLastSavedAt] = useState(0);
  const [ambienceEnabled, setAmbienceEnabledState] = useState(() => {
    try { return window.localStorage.getItem(SOUND_PREF_KEY) !== "off"; } catch { return true; }
  });
  const lastTickRef = useRef<number>(Date.now());
  const savedRef = useRef(true);

  const setAmbienceEnabled = (value: boolean) => {
    setAmbienceEnabledState(value);
    try { window.localStorage.setItem(SOUND_PREF_KEY, value ? "on" : "off"); } catch { /* ignore */ }
    if (!value) stopForestAmbience();
  };

  const [ambienceKind, setAmbienceKindState] = useState<AmbienceKind>(() => {
    try {
      const value = window.localStorage.getItem(SOUND_KIND_KEY) as AmbienceKind | null;
      if (value && ["forest", "rain", "waves", "wind"].includes(value)) return value as AmbienceKind;
    } catch { /* ignore */ }
    return "forest";
  });

  const setAmbienceKind = (kind: AmbienceKind) => {
    setAmbienceKindState(kind);
    try { window.localStorage.setItem(SOUND_KIND_KEY, kind); } catch { /* ignore */ }
    if (state.running && !state.paused && ambienceEnabled) startForestAmbience(kind);
  };

  const persist = (next: FocusPersisted) => {
    setState(next);
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* storage full/unavailable */ }
    // Background pipeline: while RUNNING, persist the wall-clock deadline so a
    // closed tab/app still gets its end notification; cleared otherwise.
    storeFocusEnd(next.running && !next.paused ? { endsAtMs: Date.now() + next.remainingMs, durationMinutes: next.durationMinutes, goal: next.goal, courseId: next.courseId || null, treeKind: next.treeKind || "pine" } : null);
  };

  useEffect(() => {
    if (!state.running || state.paused) {
      lastTickRef.current = Date.now();
      setSecondsLeft(Math.ceil(state.remainingMs / 1000));
      return;
    }
    const timer = window.setInterval(() => {
      const now = Date.now();
      const delta = now - lastTickRef.current;
      lastTickRef.current = now;
      setState((current) => {
        if (!current.running || current.paused) return current;
        const remainingMs = Math.max(0, current.remainingMs - delta);
        if (remainingMs === 0 && !savedRef.current) {
          savedRef.current = true;
          stopForestAmbience();
          playTimerDoneSound();
          notifySessionDone(current.durationMinutes, t("focus.doneTitle"), t("focus.doneBody"), locale);
          if (!isDemoMode()) {
            // The timer POST is fire-and-forget BELOW on purpose, but its failure
            // now stashes the session for a retry on next entry — the reward
            // loop (tree + stats) used to silently lose the whole session.
            const actualSeconds = Math.round((current.durationMinutes * 60_000) / 1000);
            const payload = { courseId: current.courseId || null, goal: current.goal || "Focus", plannedMinutes: current.durationMinutes, actualSeconds, status: "completed", treeType: current.treeKind || "pine" };
            void api("/focus-sessions", { method: "POST", body: JSON.stringify(payload) })
              .then(() => setLastSavedAt(Date.now()))
              .catch(() => stashPendingSave(payload));
          } else {
            setLastSavedAt(Date.now());
          }
          const finished = { ...current, remainingMs: 0, running: false, paused: false };
          try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(finished)); } catch { /* ignore */ }
          window.setTimeout(() => setLastSavedAt((value) => value), 0);
          return finished;
        }
        return { ...current, remainingMs };
      });
    }, 500);
    return () => window.clearInterval(timer);
  }, [state.running, state.paused]);

  useEffect(() => {
    setSecondsLeft(Math.ceil(state.remainingMs / 1000));
  }, [state.remainingMs]);

  const value = useMemo<FocusTimerValue>(() => ({
    durationMinutes: state.durationMinutes,
    secondsLeft,
    running: state.running,
    paused: state.paused,
    courseId: state.courseId,
    goal: state.goal,
    lastSavedAt,
    treeKind: state.treeKind ?? "pine",
    ambienceEnabled,
    ambienceKind,
    setAmbienceEnabled,
    setAmbienceKind,
    start: (durationMinutes, courseId, goal, treeKind) => {
      savedRef.current = false;
      lastTickRef.current = Date.now();
      playStartSound();
      if (ambienceEnabled) startForestAmbience(ambienceKind);
      persist({ durationMinutes, remainingMs: durationMinutes * 60_000, running: true, paused: false, courseId, goal, treeKind });
    },
    pause: () => setState((current) => {
      const next = { ...current, paused: true };
      try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    }),
    resume: () => {
      lastTickRef.current = Date.now();
      setState((current) => {
        const next = { ...current, paused: false };
        try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
        return next;
      });
    },
    stopAndSave: async () => {
      const current = state;
      const actualSeconds = Math.max(0, Math.round((current.durationMinutes * 60_000 - current.remainingMs) / 1000));
      savedRef.current = true;
      stopForestAmbience();
      if (!isDemoMode()) {
        await api("/focus-sessions", {
          method: "POST",
          body: JSON.stringify({ courseId: current.courseId || null, goal: current.goal || "Focus", plannedMinutes: current.durationMinutes, actualSeconds, status: "completed", treeType: current.treeKind || "pine" })
        }).catch(() => undefined);
      }
      setLastSavedAt(Date.now());
      persist({ durationMinutes: current.durationMinutes, remainingMs: current.durationMinutes * 60_000, running: false, paused: false, courseId: current.courseId, goal: current.goal });
    },
    discard: () => {
      savedRef.current = true;
      stopForestAmbience();
      persist({ durationMinutes: state.durationMinutes, remainingMs: state.durationMinutes * 60_000, running: false, paused: false, courseId: state.courseId, goal: state.goal });
    }
  }), [secondsLeft, state, lastSavedAt, ambienceEnabled, ambienceKind]);

  return <FocusTimerContext.Provider value={value}>{children}</FocusTimerContext.Provider>;
}

export function useFocusTimer() {
  const value = useContext(FocusTimerContext);
  if (!value) throw new Error("useFocusTimer must be used inside FocusTimerProvider");
  return value;
}
import { clearFocusNotified, deadlineMissed, isFocusNotified, markFocusNotified, readFocusEnd, remainingUntilDeadline, storeFocusEnd } from "../lib/focusBackground";
